import React, { useState } from "react";
import {
  Stack,
  Card,
  Text,
  Divider,
  Modal,
  Switch,
  Textarea,
  Button,
  Group,
} from "@mantine/core";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { handleApiError, handleGenericError } from "../../utils/errorHandler";

// The company-types array from the overview can tag each entry's type under a
// few different keys depending on the category, so resolve them tolerantly.
const getEntryType = entry =>
  entry?.type ??
  entry?.bonus_type_category ??
  entry?.expense_type_category ??
  entry?.upfront_pay_type_category ??
  entry?.upfront_type_category ??
  entry?.upfront_pay_type ??
  entry?.bonus_type ??
  entry?.expense_type ??
  null;

const getEntryLegalText = entry =>
  entry?.document_text ?? entry?.legal_text ?? "";

const isEntryActive = entry =>
  entry?.is_active !== false && entry?.active !== false;

// Shared, data-driven list of the pay types a company admin can enable. Each
// type renders as a card; enabled types show their legal text, disabled (or not
// yet configured) types render grayed out with the legal text hidden. Clicking a
// card opens a modal to toggle the type on/off and edit its legal text, which is
// persisted to /v1/ps-company-types/{endpoint}.
function PayTypesSection({
  intro,
  types,
  companyTypes,
  endpoint,
  onTypesChange,
}) {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();

  const [editingType, setEditingType] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [legalText, setLegalText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const companyTypeItems = Array.isArray(companyTypes) ? companyTypes : [];

  const findEntry = value =>
    companyTypeItems.find(entry => getEntryType(entry) === value) ?? null;

  // Enabled types float to the top; within each group the original definition
  // order is preserved.
  const sortedTypes = [...types].sort((a, b) => {
    const aEnabled = !!findEntry(a.value) && isEntryActive(findEntry(a.value));
    const bEnabled = !!findEntry(b.value) && isEntryActive(findEntry(b.value));
    return Number(bEnabled) - Number(aEnabled);
  });

  const handleOpen = type => {
    const entry = findEntry(type.value);
    setEditingType(type);
    setIsActive(entry ? isEntryActive(entry) : false);
    setLegalText(
      entry && getEntryLegalText(entry)
        ? getEntryLegalText(entry)
        : type.documentText
    );
  };

  const handleClose = () => {
    setEditingType(null);
  };

  const handleSubmit = async () => {
    if (!editingType) return;

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(
        getApiEndpoint(`/v1/ps-company-types/${endpoint}`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            type: editingType.value,
            document_text: legalText.trim(),
            is_active: isActive,
          }),
        }
      );

      if (response.ok) {
        // The save endpoint returns the updated list for this category, so use
        // it directly rather than refetching the whole overview.
        const result = await response.json().catch(() => null);
        const updatedList =
          (result && result[`ps_company_${endpoint}_types`]) ??
          (Array.isArray(result?.data) ? result.data : null) ??
          (Array.isArray(result) ? result : null);
        if (updatedList && onTypesChange) {
          onTypesChange(updatedList);
        }
        showSuccess("Pay type updated successfully!");
        handleClose();
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to update pay type"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to update pay type");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Stack gap="md">
        {intro && (
          <Text size="sm" c="dimmed">
            {intro}
          </Text>
        )}
        <Stack gap="sm">
          {sortedTypes.map(type => {
            const entry = findEntry(type.value);
            const enabled = !!entry && isEntryActive(entry);
            const displayLegalText =
              (entry && getEntryLegalText(entry)) || type.documentText;
            return (
              <Card
                key={type.value}
                padding="md"
                radius="md"
                withBorder
                onClick={() => handleOpen(type)}
                style={{
                  cursor: "pointer",
                  opacity: enabled ? 1 : 0.5,
                }}
              >
                <Stack gap="xs">
                  <Text fw={600}>{type.label}</Text>
                  <Text size="sm">{type.description}</Text>
                  {enabled && (
                    <>
                      <Divider />
                      <Stack gap={2}>
                        <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                          Legal Document Text
                        </Text>
                        <Text size="sm" c="dimmed">
                          {displayLegalText}
                        </Text>
                      </Stack>
                    </>
                  )}
                </Stack>
              </Card>
            );
          })}
        </Stack>
      </Stack>

      <Modal
        opened={!!editingType}
        onClose={handleClose}
        title={editingType?.label}
        size="lg"
      >
        <Stack gap="md">
          <Switch
            label="Enabled"
            checked={isActive}
            onChange={e => setIsActive(e.currentTarget.checked)}
          />
          <Textarea
            label="Legal Document Text"
            placeholder="Enter the legal document text"
            value={legalText}
            onChange={e => setLegalText(e.target.value)}
            minRows={4}
            autosize
          />
          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={submitting}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export default PayTypesSection;
